import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/material';
import './P2PNavigation.css';

interface P2PRoute {
  path: string;
  label: string;
}

const P2P_ROUTES: P2PRoute[] = [
  { path: '/p2p', label: 'Dashboard' },
  { path: '/p2p/offers', label: 'Browse Offers' },
  { path: '/p2p/trades', label: 'My Trades' },
];

// Helper to normalize dynamic routes
const normalizePath = (path: string): string => {
  if (path.startsWith('/p2p/offers/') && path !== '/p2p/offers' && path !== '/p2p/offers/create') {
    return '/p2p/offers';
  }
  if (path.startsWith('/p2p/trades/') && path !== '/p2p/trades') {
    return '/p2p/trades';
  }
  if (path === '/p2p/offers/create') {
    return '/p2p/offers'; // Treat create as part of offers
  }
  return path;
};

const P2PNavigation: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();

  // Get current route index
  const getCurrentRouteIndex = (): number => {
    const normalizedPath = normalizePath(location.pathname);
    return P2P_ROUTES.findIndex(r => r.path === normalizedPath);
  };

  const currentIndex = getCurrentRouteIndex();
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < P2P_ROUTES.length - 1;
  
  // Don't show navigation if we're not on a recognized P2P route
  if (currentIndex === -1) {
    return null;
  }

  const handlePrevious = () => {
    if (hasPrevious) {
      // Special cases
      const normalizedPath = normalizePath(location.pathname);
      // From /p2p/trades, go directly to /p2p (Dashboard)
      if (normalizedPath === '/p2p/trades') {
        navigate('/p2p');
        return;
      }
      // From offer details, go back to offers list
      if (location.pathname.startsWith('/p2p/offers/') && location.pathname !== '/p2p/offers') {
        navigate('/p2p/offers');
        return;
      }
      navigate(P2P_ROUTES[currentIndex - 1].path);
    }
  };

  const handleNext = () => {
    if (hasNext) {
      navigate(P2P_ROUTES[currentIndex + 1].path);
    }
  };

  // Don't show navigation on paypal-payment page
  if (location.pathname.includes('/p2p/paypal-payment')) {
    return null;
  }

  return (
    <div className="p2p-navigation">
      <IconButton
        onClick={handlePrevious}
        disabled={!hasPrevious}
        disableRipple
        title={hasPrevious ? P2P_ROUTES[currentIndex - 1]?.label : 'No previous page'}
        className="p2p-nav-icon-btn p2p-nav-prev"
        sx={{
          padding: '25px',
          fontSize: '24px',
          color: theme.palette.text.primary,
          opacity: !hasPrevious ? 0.4 : 1,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
          },
          '&:disabled': {
            cursor: 'not-allowed',
          },
        }}
      >
        <ChevronLeftIcon fontSize="large" />
      </IconButton>
      
      <IconButton
        onClick={handleNext}
        disabled={!hasNext}
        disableRipple
        title={hasNext ? P2P_ROUTES[currentIndex + 1]?.label : 'No next page'}
        className="p2p-nav-icon-btn p2p-nav-next"
        sx={{
          padding: '25px',
          fontSize: '24px',
          color: theme.palette.text.primary,
          opacity: !hasNext ? 0.4 : 1,
          '&:hover': {
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
          },
          '&:disabled': {
            cursor: 'not-allowed',
          },
        }}
      >
        <ChevronRightIcon fontSize="large" />
      </IconButton>
    </div>
  );
};

export default P2PNavigation;
